import React, { type FC } from 'react'
import { Box } from '@moonpig/launchpad-components'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { useCoreLocaleText } from '@moonpig/web-core-locale-text'
import { breakpoint, styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { getProductDisplayImageUrl } from '../../../utils/getProductDisplayImageUrl'
import type { ProductInfoProduct } from '../../../types'

export const StyledGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  ${breakpoint('md')} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  ${s({
    gap: 16,
  })}
`

export const StyledCell = styled(Box)`
  position: relative;
  min-width: 0;

  &::before {
    content: '';
    display: block;
    padding-top: 150%;
  }
`

const StyledProductButton = styled.button`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
`

const StyledImage = styled.img`
  display: block;
  width: 100%;
  ${s({ boxShadow: 2 })}
`

type GridProduct = Pick<
  ProductInfoProduct,
  'id' | 'title' | 'dynamicImages'
> & {
  masterVariant: ProductInfoProduct['masterVariant']
}

type Props = {
  products: GridProduct[]
  onProductClick: (index: number) => void
}

export const ProductGrid: FC<Props> = ({ products, onProductClick }) => {
  const commonLocaliseText = useCoreLocaleText()
  const isDynamicImagesEnabled = useExperiment('search-use-dynamic-images', {
    fallback: false,
  })

  return (
    <StyledGrid>
      {products.map((product, index) => {
        const { id, title } = product
        const imageUrl = getProductDisplayImageUrl(
          product,
          isDynamicImagesEnabled,
        )
        if (!imageUrl) {
          return null
        }

        return (
          <StyledCell key={id}>
            <StyledProductButton
              type="button"
              onClick={() => onProductClick(index)}
              aria-label={commonLocaliseText('common.button_to_select_title', {
                title,
              })}
            >
              <StyledImage alt={title} src={imageUrl} />
            </StyledProductButton>
          </StyledCell>
        )
      })}
    </StyledGrid>
  )
}
